import { useState } from 'react';
import type { WebFrequency } from '@/mocks/superAdminFrequencies';
import { saAvailableRadiosForAssignment } from '@/mocks/superAdminFrequencies';

interface AssignFrequencyModalProps {
  frequency: WebFrequency | null;
  onClose: () => void;
  onConfirm: (frequency: WebFrequency, radioId: number, radioName: string) => void;
}

export default function AssignFrequencyModal({ frequency, onClose, onConfirm }: AssignFrequencyModalProps) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<{ id: number; name: string } | null>(null);

  if (!frequency) return null;

  const filtered = saAvailableRadiosForAssignment.filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={onClose}></div>
      <div
        className="relative bg-background-50 rounded-2xl w-full max-w-md shadow-xl overflow-hidden"
        style={{ animation: 'fadeInUp 0.2s ease-out' }}
      >
        <div className="p-5 border-b border-background-200">
          <h3 className="text-foreground-950 font-bold text-sm">Asignar frecuencia</h3>
          <p className="text-foreground-400 text-xs mt-0.5">
            Seleccioná la radio para <span className="font-mono text-foreground-600">{frequency.frequency}</span>
          </p>
        </div>

        <div className="p-5 space-y-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar radio..."
              className="w-full pl-8 pr-3 py-2.5 bg-background-100 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
            />
          </div>

          <div className="max-h-48 overflow-y-auto space-y-1 rounded-xl border border-background-200 p-1.5">
            {filtered.length === 0 ? (
              <p className="text-foreground-400 text-xs text-center py-4">No se encontraron radios</p>
            ) : (
              filtered.map((radio) => {
                const isCurrent = frequency.assignedRadioId === radio.id;
                const isSel = selected?.id === radio.id;
                return (
                  <button
                    key={radio.id}
                    onClick={() => !isCurrent && setSelected(radio)}
                    disabled={isCurrent}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs transition-colors cursor-pointer ${
                      isCurrent
                        ? 'bg-background-100 text-foreground-300'
                        : isSel
                          ? 'bg-foreground-950 text-background-50'
                          : 'text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    <div className="w-7 h-7 rounded-full bg-background-200 flex items-center justify-center shrink-0">
                      <i className="ri-radio-line text-foreground-500 text-xs"></i>
                    </div>
                    <span className="text-left">{radio.name}</span>
                    {isCurrent && (
                      <span className="ml-auto text-[10px] text-foreground-300">Actual</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 p-5 border-t border-background-200">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={() => selected && onConfirm(frequency, selected.id, selected.name)}
            disabled={!selected}
            className={`flex-1 px-4 py-2.5 rounded-xl text-white text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              selected
                ? 'bg-foreground-950 hover:bg-foreground-800'
                : 'bg-background-200 text-foreground-300 cursor-not-allowed'
            }`}
          >
            Asignar
          </button>
        </div>
      </div>
    </div>
  );
}